import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Tag, Plus, Calendar, Bell, Trash2, PlayCircle, StopCircle } from "lucide-react";
import { CampaignSelect } from "../components/CampaignSelect";

const STATUS_COLOR = {
  active: "bg-emerald-900/60 text-emerald-200 border-emerald-700",
  scheduled: "bg-blue-900/60 text-blue-200 border-blue-700",
  expired: "bg-zinc-800 text-zinc-400 border-zinc-700",
  cancelled: "bg-red-900/60 text-red-200 border-red-700",
};

export default function PromoManagerModule() {
  const [active, setActive] = useState([]);
  const [upcoming, setUpcoming] = useState([]);
  const [all, setAll] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    name: "", description: "", discount_type: "percent", discount_value: 10,
    code: "", start_date: "", end_date: "",
    channels: [], target_audience: "Tous", expected_revenue: null, notes: "",
    campaign_id: null,
  });

  const load = async () => {
    try {
      const [a, u, all_] = await Promise.all([
        api.get("/promos/active"),
        api.get("/promos/upcoming"),
        api.get("/promos"),
      ]);
      setActive(a.data.promos);
      setUpcoming(u.data.promos);
      setAll(all_.data.promos);
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.name || !form.start_date || !form.end_date) {
      toast.error("Nom + start/end date requis"); return;
    }
    try {
      await api.post("/promos", form);
      toast.success("Promo créée · Reminder Gmail envoyé");
      setShowForm(false);
      setForm({ ...form, name: "", description: "", code: "", start_date: "", end_date: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const toggleChannel = (ch) => {
    setForm(p => ({ ...p, channels: p.channels.includes(ch) ? p.channels.filter(c => c !== ch) : [...p.channels, ch] }));
  };

  const updateStatus = async (id, status) => {
    await api.put(`/promos/${id}`, { status });
    toast.success("MAJ");
    load();
  };

  return (
    <div className="p-6 space-y-6" data-testid="promo-manager-module">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <Tag className="w-8 h-8 text-amber-400" /> Gestionnaire de Promos
          </h1>
          <p className="text-zinc-400 mt-1">
            Démarre · Termine · Camille te rappelle automatiquement par email (J-1 + jour fin)
          </p>
        </div>
        <button
          data-testid="new-promo-btn"
          onClick={() => setShowForm(!showForm)}
          className="bg-amber-600 hover:bg-amber-500 text-black font-semibold px-5 py-2.5 rounded-lg flex items-center gap-2"
        >
          <Plus className="w-4 h-4" /> Nouvelle promo
        </button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-3 gap-3">
        <KPI label="Actives" count={active.length} color="emerald" testid="kpi-active" />
        <KPI label="Programmées" count={upcoming.length} color="blue" testid="kpi-scheduled" />
        <KPI label="Total" count={all.length} color="amber" testid="kpi-total" />
      </div>

      {/* Form */}
      {showForm && (
        <div className="bg-zinc-900 border border-amber-700 rounded-xl p-5 space-y-3" data-testid="promo-form">
          <h3 className="font-semibold flex items-center gap-2"><Plus className="w-4 h-4 text-amber-400"/> Créer une promo</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Nom" value={form.name} onChange={v => setForm({...form, name: v})} testid="form-name" placeholder='ex: "Rentrée 2026 -15%"' />
            <Field label="Code promo (optionnel)" value={form.code} onChange={v => setForm({...form, code: v})} testid="form-code" placeholder="IMPACT24" />
            <Field label="Date début" type="date" value={form.start_date} onChange={v => setForm({...form, start_date: v})} testid="form-start" />
            <Field label="Date fin" type="date" value={form.end_date} onChange={v => setForm({...form, end_date: v})} testid="form-end" />
            <div>
              <label className="text-xs uppercase text-zinc-500 block mb-1">Type</label>
              <select value={form.discount_type} onChange={e => setForm({...form, discount_type: e.target.value})} className="w-full bg-zinc-950 border border-zinc-700 rounded-lg px-3 py-2 text-sm">
                <option value="percent">% pourcentage</option>
                <option value="flat">$ montant fixe</option>
                <option value="bundle">Bundle (kit)</option>
                <option value="free">Gratuit</option>
              </select>
            </div>
            <Field label="Valeur" type="number" value={form.discount_value} onChange={v => setForm({...form, discount_value: parseFloat(v)||0})} testid="form-value" />
            <Field label="Audience cible" value={form.target_audience} onChange={v => setForm({...form, target_audience: v})} testid="form-audience" />
            <Field label="Revenu attendu (CAD)" type="number" value={form.expected_revenue || ""} onChange={v => setForm({...form, expected_revenue: parseFloat(v)||null})} testid="form-revenue" />
            <CampaignSelect value={form.campaign_id} onChange={v => setForm({...form, campaign_id: v})} testid="promo-campaign-select" />
          </div>
          <div>
            <label className="text-xs uppercase text-zinc-500 block mb-1">Canaux à publier</label>
            <div className="flex flex-wrap gap-2">
              {["facebook","instagram","email","sms","wordpress","tiktok"].map(ch => (
                <button key={ch} type="button" onClick={() => toggleChannel(ch)} className={`text-xs px-3 py-1 rounded-full border ${form.channels.includes(ch) ? "bg-amber-500 border-amber-400 text-black" : "bg-zinc-900 border-zinc-700 text-zinc-400"}`}>{ch}</button>
              ))}
            </div>
          </div>
          <Field label="Description" value={form.description} onChange={v => setForm({...form, description: v})} testid="form-desc" />
          <Field label="Notes internes" value={form.notes} onChange={v => setForm({...form, notes: v})} testid="form-notes" />
          <div className="flex gap-2 pt-2">
            <button onClick={submit} data-testid="submit-promo" className="bg-amber-600 hover:bg-amber-500 text-black px-4 py-2 rounded-lg font-medium">Créer + Lancer reminders</button>
            <button onClick={() => setShowForm(false)} className="text-zinc-400">Annuler</button>
          </div>
        </div>
      )}

      {/* Active promos */}
      <Section title="🔥 Promos actives" testid="active-section" promos={active} updateStatus={updateStatus} showDaysRemaining />
      <Section title="🗓️ Promos programmées" testid="scheduled-section" promos={upcoming} updateStatus={updateStatus} />
      <Section title="📚 Historique (toutes)" testid="all-section" promos={all.filter(p => !active.find(a => a.id === p.id) && !upcoming.find(u => u.id === p.id))} updateStatus={updateStatus} />
    </div>
  );
}

function Section({ title, testid, promos, updateStatus, showDaysRemaining }) {
  if (promos.length === 0) return null;
  return (
    <div data-testid={testid}>
      <h3 className="text-sm uppercase tracking-widest text-amber-400 mb-2 border-b border-zinc-800 pb-1">{title}</h3>
      <div className="space-y-2">
        {promos.map(p => (
          <div key={p.id} className="bg-zinc-900 border border-zinc-800 rounded-lg p-4 hover:border-amber-700 transition" data-testid={`promo-${p.id}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <strong className="text-zinc-100">{p.name}</strong>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full border ${STATUS_COLOR[p.status]||"bg-zinc-800"}`}>{p.status}</span>
                  {p.code && <span className="text-[10px] font-mono bg-amber-900/40 border border-amber-700 text-amber-300 px-2 py-0.5 rounded">{p.code}</span>}
                </div>
                <div className="text-xs text-zinc-400">
                  <Calendar className="inline w-3 h-3 mr-1"/>
                  {p.start_date} → {p.end_date}
                  {showDaysRemaining && p.days_remaining != null && (
                    <span className="ml-2 text-amber-300">· {p.days_remaining === 0 ? "FINIT AUJOURD'HUI" : `${p.days_remaining}j restants`}</span>
                  )}
                </div>
                <div className="text-xs text-zinc-500 mt-1">
                  <strong>{p.discount_value}{p.discount_type === "percent" ? "%" : "$"}</strong> · {p.target_audience} · {(p.channels||[]).join(", ")||"—"}
                </div>
                {p.description && <div className="text-xs text-zinc-400 mt-2 italic">{p.description}</div>}
                {Object.keys(p.reminders_sent||{}).length > 0 && (
                  <div className="text-[10px] text-emerald-400 mt-1 flex items-center gap-1">
                    <Bell className="w-3 h-3"/> Reminders envoyés : {Object.keys(p.reminders_sent).join(", ")}
                  </div>
                )}
              </div>
              <div className="flex gap-1 shrink-0">
                {p.status === "scheduled" && <button onClick={() => updateStatus(p.id, "active")} className="text-emerald-400 hover:text-emerald-300 text-xs flex items-center gap-1"><PlayCircle className="w-3 h-3"/> Activer</button>}
                {p.status === "active" && <button onClick={() => updateStatus(p.id, "expired")} className="text-amber-400 hover:text-amber-300 text-xs flex items-center gap-1"><StopCircle className="w-3 h-3"/> Terminer</button>}
                {p.status !== "archived" && <button onClick={() => updateStatus(p.id, "archived")} className="text-zinc-500 hover:text-red-400 text-xs"><Trash2 className="w-3 h-3"/></button>}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function KPI({ label, count, color, testid }) {
  const c = { emerald:"text-emerald-400 border-emerald-900", blue:"text-blue-400 border-blue-900", amber:"text-amber-400 border-amber-900" }[color];
  return (
    <div className={`bg-zinc-900 border rounded-lg p-4 ${c}`} data-testid={testid}>
      <div className="text-3xl font-bold">{count}</div>
      <div className="text-xs uppercase tracking-wider text-zinc-500">{label}</div>
    </div>
  );
}

function Field({ label, value, onChange, type="text", placeholder, testid }) {
  return (
    <div>
      <label className="text-xs uppercase text-zinc-500 block mb-1">{label}</label>
      <input data-testid={testid} type={type} value={value||""} onChange={e => onChange(e.target.value)} placeholder={placeholder}
        className="w-full bg-zinc-950 border border-zinc-700 rounded-lg px-3 py-2 text-sm focus:border-amber-500 focus:outline-none"/>
    </div>
  );
}
